import { Card } from "@mui/material";
import { type NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { type GetServerSideProps } from "nextjs-routes";
import React, { useEffect, useState } from "react";
import { useLocalStorage } from "react-use";
import slugify from "slugify";
import { GlobalSalaryConverter } from "~/components/global-salary-converter/GlobalSalaryConverter";
import { value } from "~/components/helpers";
import { Layout } from "~/components/layout/Layout";
import { Button } from "~/components/ui/core/Button";
import { LegacyHeading } from "~/components/ui/core/LegacyHeading";
import { LegacyModal } from "~/components/ui/core/LegacyModal";
import { LegacyText } from "~/components/ui/core/LegacyText";
import { Link } from "~/components/ui/core/Link";
import { FaqItem } from "~/components/ui/FaqItem";
import { HubspotForm } from "~/components/ui/HubspotForm";
import { LoadingOverlay } from "~/components/ui/LoadingOverlay";
import { AllowPageIndexing } from "~/components/ui/Meta";
import { config } from "~/config";
import { useMoney } from "~/hooks/useMoney";
import { useSession } from "~/hooks/useSession";
import { type AppContext } from "~/lib/context";
import { ssr } from "~/lib/ssr";
import { buildFiguresUrl } from "~/lib/url";
import { type GetLandingStatsResponse } from "~/pages/api/get-landing-stats";
import { type EmployeeLocationWithCountry } from "~/services/employee/employeeLocation";
import { getLandingStats } from "~/services/home/getLandingStats";
import { convertSalary, type ConvertSalaryResponse } from "~/services/salaryConversion";

const fetchLocation = async (ctx: AppContext, name: string) => {
  return ctx.prisma.employeeLocation.findFirst({
    include: {
      stats: true,
      country: {
        include: {
          defaultCurrency: true,
        },
      },
    },
    where: {
      name: {
        contains: name.replace("-", " "),
        mode: "insensitive",
      },
    },
  });
};

const handler: GetServerSideProps<Props, "/global-salary-converter/[from]/[to]/[amount]"> = async (ctx) => {
  const params = ctx.params?.params ?? [];

  const amount = value(() => {
    if (!params[2]) {
      return 50000;
    }

    return parseInt(params[2]);
  });

  const fromLocation = await fetchLocation(ctx.req, params[0] ?? "Paris");
  const toLocation = await fetchLocation(ctx.req, params[1] ?? "Berlin");

  if (!fromLocation || !toLocation) {
    return { notFound: true };
  }

  const data = await convertSalary(ctx.req, {
    fromLocation,
    toLocation,
    amount,
  });

  const landingStats = await getLandingStats(ctx.req);

  return {
    props: {
      initialValues: {
        amount,
        fromLocation,
        toLocation,
      },
      landingStats,
      data,
    },
  };
};

export const getServerSideProps = ssr(handler, {
  authentication: { optional: true },
});

type FormValues = {
  amount: number;
  fromLocation: EmployeeLocationWithCountry | null;
  toLocation: EmployeeLocationWithCountry | null;
};

type Props = {
  initialValues: FormValues;
  data: ConvertSalaryResponse;
  landingStats: GetLandingStatsResponse;
};

const Page: NextPage<Props> = ({ initialValues, data, landingStats }) => {
  const router = useRouter();
  const { user } = useSession();

  const [conversionsCount, setConversionsCount] = useState(0);
  const [showModal, setShowModal] = useLocalStorage("global-salary-convertor.show", false);
  const [hasFilledForm, setHasFilledForm] = useLocalStorage("global-salary-convertor.filled", false);

  useEffect(() => {
    if (conversionsCount <= 5 || showModal || hasFilledForm || user) {
      return;
    }

    setShowModal(true);
  }, [hasFilledForm, showModal, conversionsCount > 5, user]);

  return (
    <Layout title="Global Salary Converter">
      <Meta data={data} initialValues={initialValues} landingStats={landingStats} />
      <AllowPageIndexing />

      <LegacyModal
        className="w-[800px]"
        isOpen={showModal ?? false}
        title="👋  Hey there"
        onClose={() => {
          setShowModal(false);
        }}
      >
        <div className="relative px-1">
          {/* Focusable input to enable FocusTrap from the modal since Hubspot form loads asynchronously */}
          <div className="h-[1px] w-[1px] overflow-hidden opacity-0">
            <input type="text" />
          </div>
          <HubspotForm
            formId={config.hubspot.forms.globalSalaryConverter}
            loading={<LoadingOverlay delay={300} title="Loading..." />}
            portalId={config.hubspot.forms.portalId}
            onFormSubmitted={() => {
              setHasFilledForm(true);
              setShowModal(false);
            }}
          />
        </div>
      </LegacyModal>
      <div className="container mx-auto mb-20 xl:max-w-5xl">
        <GlobalSalaryConverter
          initialData={data}
          initialValues={initialValues}
          initialLandingStats={landingStats}
          onChange={(formValues) => {
            if (!formValues.amount || !formValues.fromLocation || !formValues.toLocation) {
              return;
            }

            const from = slugify(formValues.fromLocation.name).toLowerCase();
            const to = slugify(formValues.toLocation.name).toLowerCase();

            void router.replace({
              pathname: `/global-salary-converter/[from]/[to]/[amount]`,
              query: {
                from,
                to,
                amount: `${formValues.amount}`,
              },
            });

            setConversionsCount(conversionsCount + 1);
          }}
        />

        <Card className="mt-12">
          <LegacyHeading className="font-title" size="2xl">
            What's Figures?
          </LegacyHeading>

          <LegacyText className="mt-4 border-t pt-4">
            Figures simplifies and streamlines compensation management and communication. It includes a compensation
            playbook with +1,200 participant companies in our Market Data Benchmark, a full Salary Bands builder with a
            sharing centre, and a powerful Compensation Planning budget calculator to define your next salary increase
            budget, all in one easy-to-use solution. Trusted by thousands of top European businesses, Figures saves time
            and money by driving more efficient and fair people reward decisions.
          </LegacyText>

          <div className="flex space-x-2">
            <Button
              component={Link}
              variant="contained"
              size="small"
              className="mt-4 block"
              newTab
              to={buildFiguresUrl()}
            >
              Learn more &rarr;
            </Button>
          </div>
        </Card>

        <div className="mt-12 flex flex-col border-t border-gray-100 pt-12">
          <LegacyHeading icon="chat-question" className="mb-4" size="xl">
            Frequently asked questions
          </LegacyHeading>

          <FaqItem
            answer={
              <>
                <span>
                  The compensation management solution designed for Mid-market and Enterprise businesses, enabling you
                  to effectively manage rewards and inspire your team's success.
                </span>
                <br />
                <Button
                  component={Link}
                  variant="contained"
                  size="small"
                  className="mt-4"
                  newTab
                  to={buildFiguresUrl()}
                >
                  Learn more &rarr;
                </Button>
              </>
            }
            question="What is Figures?"
          />
          <FaqItem
            answer="There is no perfect solution, however cost of living is often used in case of international mobility (with an objective of maintaining the person's purchasing power in the new location), while cost of labor is often preferrable when it comes to hiring local talent"
            question="Should I use cost of labor or cost of living?"
          />
          <FaqItem
            answer="You should use gross salary. We do not yet account for gross to net salary differences as they greatly differ from a country to another."
            question="Should I use net or gross salary?"
          />
          <FaqItem
            answer="The total amount of money that it costs a company to employ people, including pay, insurance, benefits etc."
            question="What's the employer cost?"
          />
          <FaqItem
            question="How do you take into account location for your employees fixed salary?"
            answer={
              <>
                That’s for you to define as part of your{" "}
                <Link newTab to="https://figures.hr/post/what-is-a-compensation-philosophy-and-how-to-set-it-up">
                  compensation philosophy
                </Link>
                .
              </>
            }
          />
          <FaqItem
            question="What are some international compensation best practices?"
            answer={
              <div>
                <ul className="list-disc pl-4">
                  <li>Aligning your compensation philosophy for fairness</li>
                  <li> Defining your remote compensation strategy </li>
                  <li> Communicating clearly to your employees</li>
                  <li> Evaluation & feedback</li>
                </ul>
                <Button
                  component={Link}
                  variant="contained"
                  size="small"
                  className="mt-4"
                  newTab
                  to="https://figures.hr/post/how-to-compensate-your-international-team"
                >
                  Learn more &rarr;
                </Button>
              </div>
            }
          />
        </div>
      </div>
    </Layout>
  );
};

const Meta: React.FC<Props> = ({ initialValues, data }) => {
  const money = useMoney(data.local.currency);
  const title = "Figures · Global Salary Converter";
  const amount = money.format(initialValues.amount * 100);
  const description = `How much should an employee based in ${initialValues.fromLocation?.name} with a base salary of ${amount} earn in ${initialValues.toLocation?.name}? Our Figures Global Salary Converter is here to help you out!`;

  return (
    <Head>
      <meta key="description" content={description} name="description" />
      <meta key="ogTitle" content={title} property="og:title" />
      <meta key="ogSiteName" content={title} property="og:site_name" />
      <meta key="ogDescription" content={description} property="og:description" />
      <meta key="ogType" content="website" property="og:type" />
      <meta key="ogImage" content="https://app.figures.hr/images/og-global-salary-convertor.png" property="og:image" />
    </Head>
  );
};

export default Page;
